(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canObserve = 'IntersectionObserver' in window;

  // ---------- Scroll reveal ----------
  const io = canObserve && !reduce
    ? new IntersectionObserver((entries) => {
        let n = 0;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.style.setProperty('--dv-delay', `${Math.min(n++, 6) * 0.07}s`);
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;

  const reveal = (el) => {
    if (!io || el.nodeType !== 1 || el.classList.contains('dv-reveal')) return;
    el.classList.add('dv-reveal');
    io.observe(el);
  };

  // ---------- Homepage slider: on mobile, drop slides that have no mobile image ----------
  const mobileSlides = (tries = 0) => {
    const el = document.querySelector('[id^="Slideshow-"]');
    if (!el || !el.querySelector('.img_mobile')) return;
    if (!window.jQuery || !el.classList.contains('slick-initialized')) {
      if (tries < 40) setTimeout(() => mobileSlides(tries + 1), 150);
      return;
    }
    const $el = window.jQuery(el);
    const mq = window.matchMedia('(max-width: 749px)');
    let filtered = false;
    const apply = () => {
      if (mq.matches && !filtered) {
        $el.slick('slickFilter', (i, slide) => !!slide.querySelector('.img_mobile'));
        filtered = true;
      } else if (!mq.matches && filtered) {
        $el.slick('slickUnfilter');
        filtered = false;
      }
    };
    apply();
    mq.addEventListener('change', apply);
  };
  mobileSlides();

  // ---------- Footer: parallax frames, cursor glow, reveal ----------
  document.querySelectorAll('[data-dvf]').forEach((footer) => {
    const enter = () => footer.classList.add('is-in');
    if (canObserve && !reduce) {
      const fo = new IntersectionObserver(([e]) => { if (e.isIntersecting) { enter(); fo.disconnect(); } }, { threshold: 0.08 });
      fo.observe(footer);
    } else {
      enter();
    }
    if (reduce || !window.matchMedia('(hover: hover)').matches) return;
    let raf = 0;
    footer.addEventListener('pointermove', (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const r = footer.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width - 0.5;
        const ny = (e.clientY - r.top) / r.height - 0.5;
        footer.style.setProperty('--mx', `${e.clientX - r.left}px`);
        footer.style.setProperty('--my', `${e.clientY - r.top}px`);
        footer.style.setProperty('--px', (nx * -30).toFixed(1));
        footer.style.setProperty('--py', (ny * -30).toFixed(1));
      });
    });
  });

  // Footer accordions: open on desktop, collapsed on mobile
  const accMq = window.matchMedia('(max-width: 749px)');
  const setAcc = () => document.querySelectorAll('.dvf__acc').forEach((d) => { d.open = !accMq.matches; });
  setAcc();
  accMq.addEventListener('change', setAcc);

  // ---------- Help pages: reading progress, TOC + scroll-spy, FAQ accordion + search ----------
  const dvh = document.querySelector('[data-dvh]');
  if (dvh) {
    const content = dvh.querySelector('[data-dvh-content]');
    const bar = document.querySelector('[data-dvh-progress]');
    if (bar && content) {
      const onScroll = () => {
        const r = content.getBoundingClientRect();
        const total = r.height - window.innerHeight * 0.6;
        const p = Math.min(1, Math.max(0, -r.top / (total > 0 ? total : 1)));
        bar.style.setProperty('--p', p.toFixed(3));
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    const slug = (t, i) => `${t.toLocaleLowerCase('tr').replace(/[^a-z0-9çğıöşü]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'bolum'}-${i}`;

    if (dvh.classList.contains('dvh--faq') && content) {
      // Wrap each h2 and the nodes after it into a <details>; the text itself is untouched
      const heads = [...content.querySelectorAll(':scope > h2')];
      heads.forEach((h, i) => {
        const details = document.createElement('details');
        details.className = 'dvh-q';
        details.id = slug(h.textContent, i);
        const summary = document.createElement('summary');
        const answer = document.createElement('div');
        answer.className = 'dvh-q__a';
        let n = h.nextSibling;
        while (n && !(n.nodeType === 1 && /^H[12]$/.test(n.tagName))) {
          const next = n.nextSibling;
          answer.appendChild(n);
          n = next;
        }
        h.replaceWith(details);
        summary.appendChild(h);
        details.append(summary, answer);
      });
      if (location.hash) {
        const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
        if (target && target.tagName === 'DETAILS') target.open = true;
      }
      const box = dvh.querySelector('[data-dvh-search]');
      if (box && heads.length) {
        box.hidden = false;
        const input = box.querySelector('input');
        const empty = box.querySelector('.dvh__search-empty');
        const items = [...content.querySelectorAll('.dvh-q')];
        input.addEventListener('input', () => {
          const q = input.value.trim().toLocaleLowerCase('tr');
          let shown = 0;
          items.forEach((d) => {
            const hit = !q || d.textContent.toLocaleLowerCase('tr').includes(q);
            d.hidden = !hit;
            if (hit) shown++;
            if (q && hit && !d.open && d.querySelector('summary').textContent.toLocaleLowerCase('tr').includes(q) === false) d.open = true;
          });
          empty.hidden = shown > 0;
        });
      }
    } else if (content) {
      // Table of contents from h2 headings with scroll-spy
      const toc = dvh.querySelector('[data-dvh-toc]');
      const heads = [...content.querySelectorAll('h2')];
      if (toc && heads.length > 2) {
        const list = toc.querySelector('ol');
        const links = heads.map((h, i) => {
          if (!h.id) h.id = slug(h.textContent, i);
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = `#${h.id}`;
          a.textContent = h.textContent.replace(/^\s*\d+\.\s*/, '');
          li.appendChild(a);
          list.appendChild(li);
          return a;
        });
        toc.hidden = false;
        if (canObserve) {
          const spy = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              const i = heads.indexOf(e.target);
              links.forEach((a, j) => a.classList.toggle('is-active', j === i));
            });
          }, { rootMargin: '-140px 0px -65% 0px' });
          heads.forEach((h) => spy.observe(h));
        }
      }
    }
  }

  // ---------- About page: reveal, counters, hero tilt ----------
  const dva = document.querySelector('[data-dva]');
  if (dva) {
    const items = [...dva.querySelectorAll('[data-dva-reveal]')];
    const counters = [...dva.querySelectorAll('[data-dva-count]')];
    const count = (el) => {
      const end = parseInt(el.dataset.dvaCount, 10);
      if (reduce || !end) return;
      const t0 = performance.now();
      const dur = 1100;
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / dur);
        el.textContent = Math.round(end * (1 - Math.pow(1 - k, 3)));
        if (k < 1) requestAnimationFrame(tick);
      };
      el.textContent = '0';
      requestAnimationFrame(tick);
    };
    if (canObserve && !reduce) {
      const ro = new IntersectionObserver((entries) => {
        let n = 0;
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.style.setProperty('--dva-d', `${Math.min(n++, 4) * 0.08}s`);
          e.target.classList.add('is-in');
          e.target.querySelectorAll('[data-dva-count]').forEach(count);
          ro.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
      items.forEach((el) => ro.observe(el));
    } else {
      items.forEach((el) => el.classList.add('is-in'));
    }
    const tilt = dva.querySelector('[data-dva-tilt]');
    if (tilt && !reduce && window.matchMedia('(hover: hover)').matches) {
      tilt.addEventListener('pointermove', (e) => {
        const r = tilt.getBoundingClientRect();
        tilt.style.setProperty('--rx', `${((e.clientX - r.left) / r.width - 0.5) * 6}deg`);
        tilt.style.setProperty('--ry', `${((e.clientY - r.top) / r.height - 0.5) * -6}deg`);
      });
      tilt.addEventListener('pointerleave', () => {
        tilt.style.setProperty('--rx', '0deg');
        tilt.style.setProperty('--ry', '0deg');
      });
    }
  }

  // ---------- Homepage design request form ----------
  document.querySelectorAll('[data-dvr]').forEach((box) => {
    if (canObserve && !reduce) {
      const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { box.classList.add('is-in'); o.disconnect(); } }, { threshold: 0.12 });
      o.observe(box);
    } else {
      box.classList.add('is-in');
    }
    const field = box.querySelector('[data-dvr-theme-field]');
    const syncThemes = () => {
      if (field) field.value = [...box.querySelectorAll('[data-dvr-theme]:checked')].map((c) => c.value).join(', ');
    };
    box.addEventListener('change', (e) => { if (e.target.matches('[data-dvr-theme]')) syncThemes(); });
    const area = box.querySelector('textarea');
    const counter = box.querySelector('[data-dvr-count]');
    if (area && counter) {
      const upd = () => { counter.textContent = area.value.length; };
      area.addEventListener('input', upd);
      upd();
    }
    // After Shopify redirects back with ?contact_posted=true, bring the result into view
    const done = box.querySelector('[data-dvr-success]') || box.querySelector('.dvr__error');
    if (done && /contact_posted=true|DesignRequestForm/.test(location.href)) {
      box.classList.add('is-in');
      setTimeout(() => { box.scrollIntoView({ block: 'center' }); if (done.focus) done.focus({ preventScroll: true }); }, 300);
    }
  });

  // ---------- Wishlist page (reads the theme's localStorage list) ----------
  const dvw = document.querySelector('[data-dvw]');
  if (dvw) {
    const KEY = 'localWishlist';
    const grid = dvw.querySelector('[data-dvw-grid]');
    const empty = dvw.querySelector('[data-dvw-empty]');
    const countEl = dvw.querySelector('[data-dvw-count]');
    const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } };
    const write = (list) => {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* storage blocked */ }
      document.querySelectorAll('.js-wishlist-count').forEach((el) => { el.textContent = list.length; });
    };
    const money = (cents) => (window.theme && theme.Currency && theme.moneyFormat)
      ? theme.Currency.formatMoney(cents, theme.moneyFormat)
      : `${(cents / 100).toFixed(2)}TL`;
    const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const img = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
    const setCount = (n) => {
      countEl.hidden = n === 0;
      countEl.textContent = `${n} tasarım`;
      empty.hidden = n !== 0;
      grid.hidden = n === 0;
    };

    const card = (p, i) => {
      const firstAvailable = p.variants.find((v) => v.available);
      const single = p.variants.length === 1;
      const price = p.price_varies
        ? `<b>${money(p.price_min)}</b><span>'den başlayan</span>`
        : `<b>${money(p.price)}</b>`;
      const second = p.images && p.images[1] ? `<img class="dvw__img dvw__img--2" src="${img(p.images[1], 600)}" alt="" loading="lazy">` : '';
      let action;
      if (!p.available) action = `<span class="dvw__action dvw__action--off">Tükendi</span>`;
      else if (single) action = `<button type="button" class="dvw__action js-grid-cart" data-id="${firstAvailable.id}"><span>Sepete ekle</span></button>`;
      else action = `<a class="dvw__action" href="${p.url}">Ebat seç</a>`;
      return `
        <article class="dvw__card" style="--i:${i}" data-handle="${esc(p.handle)}">
          <a class="dvw__media" href="${p.url}">
            <img class="dvw__img" src="${img(p.featured_image, 600)}" alt="${esc(p.title)}" loading="lazy">
            ${second}
          </a>
          <button type="button" class="dvw__remove" data-dvw-remove aria-label="${esc(p.title)} ürününü listeden çıkar">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-9.2-9.4C1.7 8 3.6 4.5 7.1 4.5c2 0 3.6 1.2 4.9 3 1.3-1.8 2.9-3 4.9-3 3.5 0 5.4 3.5 4.3 6.6-1.7 4.8-9.2 9.4-9.2 9.4z" fill="currentColor"/></svg>
          </button>
          <div class="dvw__info">
            ${p.type ? `<p class="dvw__type">${esc(p.type)}</p>` : ''}
            <a class="dvw__name" href="${p.url}">${esc(p.title)}</a>
            <p class="dvw__price">${price}</p>
            ${action}
          </div>
        </article>`;
    };

    const render = async () => {
      const list = read();
      if (!list.length) { grid.innerHTML = ''; setCount(0); return; }
      const results = await Promise.all(list.map((h) =>
        fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}products/${encodeURIComponent(h)}.js`)
          .then((r) => (r.ok ? r.json() : null)).catch(() => undefined)));
      // drop products that no longer exist (404), keep ones that failed on network
      const alive = list.filter((h, i) => results[i] !== null);
      if (alive.length !== list.length) write(alive);
      const products = results.filter(Boolean);
      grid.innerHTML = products.map(card).join('');
      setCount(products.length);
    };

    grid.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-dvw-remove]');
      if (!btn) return;
      const cardEl = btn.closest('.dvw__card');
      const handle = cardEl.dataset.handle;
      write(read().filter((h) => h !== handle));
      cardEl.classList.add('is-removing');
      setTimeout(() => {
        cardEl.remove();
        setCount(grid.querySelectorAll('.dvw__card').length);
      }, reduce ? 0 : 350);
    });

    render();
  }

  // ---------- Collection page ----------
  document.querySelectorAll('[data-dch-hero]').forEach((hero) => {
    if (reduce) return;
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      hero.style.setProperty('--mx', `${e.clientX - r.left}px`);
      hero.style.setProperty('--my', `${e.clientY - r.top}px`);
    });
  });

  const grid = document.querySelector('.template-collection #product-grid');
  if (grid && io) {
    const watch = (item) => { if (item.nodeType === 1 && item.classList.contains('grid__item')) reveal(item); };
    grid.querySelectorAll('.grid__item').forEach(watch);
    new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach(watch)))
      .observe(grid, { childList: true });
  }

  // ---------- Product page ----------
  if (!document.body.classList.contains('template-product')) return;
  const form = document.querySelector('form.product-form[id^="AddToCartForm-"]');
  if (!form) return;

  const atc = form.querySelector('[id^="AddToCart-"]');
  const priceEl = document.querySelector('.product-single__price[id^="ProductPrice-"]');
  const sticky = document.querySelector('[data-dv-sticky]');

  const selectedValues = () =>
    [...form.querySelectorAll('.single-option-selector__radio:checked, select.single-option-selector')]
      .map((i) => i.value).filter(Boolean);

  // Sections below the product reveal on scroll
  document.querySelectorAll('.template-product main > .shopify-section').forEach((s) => {
    if (!s.querySelector('.product-single')) reveal(s);
  });

  // Hover zoom on main gallery images
  if (!reduce && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.querySelectorAll('.product__photo-wrapper').forEach((wrap) => {
      const img = wrap.querySelector('img');
      if (!img) return;
      wrap.classList.add('dv-zoomable');
      wrap.addEventListener('pointermove', (e) => {
        const r = wrap.getBoundingClientRect();
        img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        img.classList.add('dv-zoom-on');
      });
      wrap.addEventListener('pointerleave', () => img.classList.remove('dv-zoom-on'));
    });
  }

  // Vertical thumbnail rail driving the theme's main gallery slider (desktop)
  const buildRail = (tries = 0) => {
    const photos = document.querySelector('.template-product .photos');
    const gallery = photos && photos.querySelector('.my-gallery');
    if (!gallery) return;
    if (!window.jQuery || !gallery.classList.contains('slick-initialized')) {
      if (tries < 40) setTimeout(() => buildRail(tries + 1), 150);
      return;
    }
    const thumbs = [...photos.querySelectorAll('.photos__item--thumbs a.product-single__thumbnail')];
    if (thumbs.length < 2 || photos.querySelector('.dv-rail')) return;
    const $gallery = window.jQuery(gallery);
    const rail = document.createElement('div');
    rail.className = 'dv-rail';
    rail.setAttribute('aria-label', 'Ürün görselleri');
    thumbs.forEach((a, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dv-rail__btn';
      btn.setAttribute('aria-label', `Görsel ${i + 1}`);
      const src = a.getAttribute('href').replace(/([?&])width=\d+/, '$1width=200');
      btn.innerHTML = `<img src="${src}" alt="" loading="lazy">`;
      btn.addEventListener('click', () => $gallery.slick('slickGoTo', i));
      rail.appendChild(btn);
    });
    const setActive = (i) => rail.querySelectorAll('.dv-rail__btn').forEach((b, j) => {
      b.classList.toggle('is-active', j === i);
      if (j !== i) return;
      if (b.offsetTop < rail.scrollTop) rail.scrollTop = b.offsetTop;
      else if (b.offsetTop + b.offsetHeight > rail.scrollTop + rail.clientHeight) rail.scrollTop = b.offsetTop + b.offsetHeight - rail.clientHeight;
    });
    photos.prepend(rail);
    photos.classList.add('dv-has-rail');
    $gallery.on('afterChange', (e, slick, current) => setActive(current));
    setActive($gallery.slick('slickCurrentSlide') || 0);
    $gallery.slick('setPosition');
  };
  buildRail();

  // Size preview: drawn to scale.
  //  - posters: above a ~200 cm sofa on a 300 x 200 cm wall
  //  - wall tapestries (product type "Duvar Örtüsü"): hanging fabric on a 400 x 280 cm floor-to-ceiling wall next to a 175 cm person
  const sizeSet = [...form.querySelectorAll('fieldset.single-option-radio')].find((fs) => {
    const inputs = [...fs.querySelectorAll('input')];
    return inputs.length && inputs.every((i) => /^\s*\d+\s*[x×]\s*\d+\s*$/i.test(i.value));
  });
  let updateSize = () => {};
  const productType = (sticky && sticky.dataset.productType) || '';
  const isFabric = /örtü/i.test(productType);
  if (sizeSet && isFabric) {
    const WALL_W = 400, WALL_H = 280, CEIL_GAP = 10, EYE = 150, FLOOR_GAP = 6, TAP_X = 165, PERSON_H = 175, PERSON_W = 46, PERSON_X = 318;
    const box = document.createElement('div');
    box.className = 'dv-size dv-size--fabric';
    box.innerHTML = `
      <div class="dv-size__head"><span>Duvarında nasıl durur?</span><strong class="dv-size__dim"></strong></div>
      <div class="dv-size__wall dv-size__wall--room" aria-hidden="true">
        <span class="dv-size__ceil"></span>
        <div class="dv-fabric">
          <span class="dv-fabric__rod"></span>
          <span class="dv-fabric__cloth"><i></i></span>
          <span class="dv-fabric__mw"><b></b></span>
          <span class="dv-fabric__mh"><b></b></span>
        </div>
        <svg class="dv-size__person" viewBox="0 0 46 175" preserveAspectRatio="none" style="left:${((PERSON_X - PERSON_W / 2) / WALL_W) * 100}%;width:${(PERSON_W / WALL_W) * 100}%;height:${(PERSON_H / WALL_H) * 100}%">
          <circle cx="23" cy="12" r="10"/>
          <path d="M11 27h24c3 0 5 2 5 5l2 52c0 3-5 3-5 0l-3-44v131c0 4-8 4-8 0V102h-6v69c0 4-8 4-8 0V40l-3 44c0 3-5 3-5 0l2-52c0-3 2-5 5-5z"/>
        </svg>
        <span class="dv-size__ref dv-size__ref--ceil">Tavan ≈ ${WALL_H} cm</span>
        <span class="dv-size__ref dv-size__ref--person">${PERSON_H} cm</span>
      </div>`;
    (sizeSet.closest('.selector-wrapper') || sizeSet).after(box);
    const fabric = box.querySelector('.dv-fabric');
    const dim = box.querySelector('.dv-size__dim');
    const mw = box.querySelector('.dv-fabric__mw b');
    const mh = box.querySelector('.dv-fabric__mh b');
    updateSize = () => {
      const checked = sizeSet.querySelector('input:checked');
      if (!checked) return;
      const [w, h] = checked.value.split(/[x×]/i).map((v) => parseFloat(v));
      // hang centred at eye level, but keep it between ceiling and floor
      let top = Math.min(WALL_H - CEIL_GAP, EYE + h / 2);
      let bottom = Math.max(FLOOR_GAP, top - h);
      top = bottom + h;
      dim.textContent = `${w} × ${h} cm`;
      mw.textContent = `${w} cm`;
      mh.textContent = `${h} cm`;
      fabric.style.left = `${((TAP_X - w / 2) / WALL_W) * 100}%`;
      fabric.style.width = `${(w / WALL_W) * 100}%`;
      fabric.style.height = `${(Math.min(h, WALL_H - FLOOR_GAP) / WALL_H) * 100}%`;
      fabric.style.bottom = `${(bottom / WALL_H) * 100}%`;
    };
    updateSize();
  } else if (sizeSet) {
    const WALL_W = 300, WALL_H = 200, SOFA_W = 200, SOFA_H = 75, GAP = 20;
    const box = document.createElement('div');
    box.className = 'dv-size';
    box.innerHTML = `
      <div class="dv-size__head"><span>Duvarında nasıl durur?</span><strong class="dv-size__dim"></strong></div>
      <div class="dv-size__wall" aria-hidden="true">
        <div class="dv-size__poster"></div>
        <div class="dv-size__sofa" style="width:${(SOFA_W / WALL_W) * 100}%;height:${(SOFA_H / WALL_H) * 100}%"><i></i></div>
        <span class="dv-size__ref">Kanepe ≈ ${SOFA_W} cm</span>
      </div>`;
    (sizeSet.closest('.selector-wrapper') || sizeSet).after(box);
    const poster = box.querySelector('.dv-size__poster');
    const dim = box.querySelector('.dv-size__dim');
    updateSize = () => {
      const checked = sizeSet.querySelector('input:checked');
      if (!checked) return;
      const [w, h] = checked.value.split(/[x×]/i).map((v) => parseFloat(v));
      dim.textContent = `${w} × ${h} cm`;
      poster.style.width = `${(w / WALL_W) * 100}%`;
      poster.style.height = `${(h / WALL_H) * 100}%`;
      poster.style.bottom = `${((SOFA_H + GAP) / WALL_H) * 100}%`;
    };
    updateSize();
  }

  // Sticky add-to-cart bar
  const syncSticky = () => {
    if (!sticky || !atc) return;
    const p = sticky.querySelector('[data-dv-sticky-price]');
    const v = sticky.querySelector('[data-dv-sticky-variant]');
    const b = sticky.querySelector('[data-dv-sticky-btn]');
    if (priceEl) p.textContent = priceEl.textContent.trim();
    if (v && v.textContent.trim() !== '') v.textContent = selectedValues().join(' / ');
    b.disabled = atc.disabled;
    b.textContent = (atc.querySelector('span[id^="AddToCartText-"]') || atc).textContent.trim();
  };

  if (priceEl) {
    new MutationObserver(() => {
      priceEl.classList.remove('dv-pop');
      void priceEl.offsetWidth;
      priceEl.classList.add('dv-pop');
      syncSticky();
    }).observe(priceEl, { childList: true, characterData: true, subtree: true });
  }

  form.addEventListener('change', () => {
    updateSize();
    setTimeout(syncSticky, 60);
  });

  if (sticky && atc && canObserve) {
    syncSticky();
    sticky.querySelector('[data-dv-sticky-btn]').addEventListener('click', () => atc.click());
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.querySelector('[data-dv-sticky-btn]').tabIndex = show ? 0 : -1;
      document.body.classList.toggle('dv-sticky-on', show);
    }).observe(atc);
  }
})();
